import * as React from "react";
import { CommonHelper } from "../../../../../../../react-core/Helpers/CommonHelper";
import { MoneyDisplayHelper } from "../../../../../../../react-core/Helpers/MoneyDisplayHelper";
import { CheckboxEditor } from "../../../../../../../react-core/Html/CheckboxEditor";
import { PartLoading } from "../../../../../../../react-core/Html/PartLoading";
import { PercentageTextBoxEditor } from "../../../../../../../react-core/Html/PercentageTextBoxEditor";
import { isNullOrEmpty } from "../../../../../../../react-core/Utils/Arrays";
import { InvestmentAllocation } from "../../../../../../../Wrap/Models/Domains/Investment/InvestmentAllocation";
import { User } from "../../../../../../../Wrap/Models/Models/User/User";
import { GENERIC_RESTRICTIONS_MESSAGE } from "../../../../../../Shared/Common/UserMessages";
import { Alert } from "../../../../../../Shared/Components/Alert";
import { AlertType } from "../../../../../../Shared/Entities/Constants/AlertConstants";
import { IProcessComponentProps, ProcessComponent } from "../../../../../../Shared/Process/Process/ProcessComponent";
import { Table } from "../../../../../../Shared/Table/Table";
import { UserStore } from "../../../../../Stroes/Variable";
import { RestrictionStore } from "../../Restrictions/Variable";
import { ChooseAssetsToSwitchAssets } from "./Store/DrawdownCalculationStoreStatic";
import { Money } from "../../../../../../../Wrap/Core/DataTypes/Money";
import { DrawdownSwitchValidationMessage } from "./Resource/Contents";
import { DeferredFundWarningMessage } from "../../Resource/Contents";
import { ProductType } from "../../../../../../../Wrap/Constants/Enums/Domain/ProductType";

interface IChooseAssetsToSwitchTableProps extends IProcessComponentProps {
    data: InvestmentAllocation[];
    tableData: ChooseAssetsToSwitchAssets[];
    switchTotalValue: number;
    pensionCommencementLumpSumAllocations: InvestmentAllocation[];
    sellTotal: number;
    onSwitchAllChange?: (row: ChooseAssetsToSwitchAssets, switchAll: boolean) => void;
    disableOffplatform?: boolean;
}

interface IChooseAssetsToSwitchAssetsState {
    userInfo: User;
    validationErrors: string[];
}

export class ChooseAssetsToSwitchAssetsTable extends ProcessComponent<
    IChooseAssetsToSwitchTableProps,
    IChooseAssetsToSwitchAssetsState
> {
    constructor(props: any) {
        super(props);
        this.state = {
            userInfo: UserStore.getUserInfo(),
            validationErrors: [],
        };
    }

    private onAssetsChanges(row: ChooseAssetsToSwitchAssets, v: number) {
        const i = this.props.data.findIndex((ele) => ele.ProductId == row.ProductId);
        const _data = this.props.data.concat();
        if (i > -1) {
            _data[i].Percentage = v;
        } else {
            _data.push({ Name: row.Assets, Percentage: v, ProductId: row.ProductId, Type: row.ProductType });
        }
        this.props.onChange(_data);
    }

    isValid() {
        const hasValidationErrors = Object.values(this.state.validationErrors).some(errors => errors !== null && errors.length > 0);
        const total = this.props.data?.reduce((sum, curr) => {
            return (sum = sum + curr.Percentage);
        }, 0);
        if (total !== 100) {
            this.props.showErrorMsgPopUp([DrawdownSwitchValidationMessage.total100]);
            return false;
        }

        if (hasValidationErrors) {
            this.props.showErrorMsgPopUp(this.state.validationErrors);
            return false;
        }

        return super.isValid() && total == 100;
    }

    disableInput = (row) => {
        return row.HasOpenValueSellOrder || row.IsDeferred || row.SwitchAll || (row.ProductType == ProductType.OffPlatform && this.props.disableOffplatform)
    };

    disableSwitchAll = (row) => {
        return row.HasOpenValueSellOrder || row.IsDeferred || (row.ProductType == ProductType.OffPlatform && this.props.disableOffplatform)
    }

    checkDeferredFunds = (assets) => {
        let deferredInstrumentCodes = assets.filter((a) => a.IsDeferred).map((o) => o.Assets);
        let message = DeferredFundWarningMessage.CannotSwitchFromDeferredFundWarning.replace(
            "<InstrumentCode>",
            deferredInstrumentCodes.join(",")
        );
        return message
    }

    handleValidation = (errors: string[]) => {
        this.setState({
            validationErrors: errors
        });
    }

    public render(): JSX.Element {
        const { data, tableData } = this.props;
        const total = data?.reduce((sum, curr) => {
            return (sum = sum + curr.Percentage);
        }, 0);

        let totalValue = this.props.switchTotalValue;

        if (isNullOrEmpty(tableData)) {
            return <PartLoading isLoading={true} />;
        }
        let _tableData = tableData.concat();

        if (RestrictionStore.sellDisabled()) {
            _tableData = [];
        }

        const investmentsWithOpenSellOrder = _tableData.length > 0
            ? _tableData.filter(
                (ele) => ele.HasOpenValueSellOrder
            )
            : [];
        const investmentsDeferred = _tableData.length > 0
            ? _tableData.filter(
                (ele) => ele.IsDeferred
            )
            : [];

        let restrictedMessage = DrawdownSwitchValidationMessage.restrict;

        if (this.state.userInfo != null && this.state.userInfo.IsAdviser) {
            restrictedMessage = GENERIC_RESTRICTIONS_MESSAGE;
        }

        return (
            <>
                <hr />
                {RestrictionStore.sellDisabled() && <Alert alertType={AlertType.Error} message={restrictedMessage} />}
                {investmentsWithOpenSellOrder.length > 0 && (
                    <Alert
                        alertType={AlertType.Warning}
                        message={`Instrument ${investmentsWithOpenSellOrder
                            .map((ele) => ele.Assets)
                            .toString()} currently has an open value sell order. No further sell instructions can be placed until this has been confirmed.`}
                    />
                )}
                {investmentsDeferred.length > 0 && (
                    <Alert
                        alertType={AlertType.Warning}
                        message={this.checkDeferredFunds(investmentsDeferred)}
                    />
                )}
                <Table
                    data={_tableData}
                    col={[
                        {
                            title: "Assets",
                            key: "Assets",
                            render: (data, row: ChooseAssetsToSwitchAssets, index) => {
                                return (
                                    <a
                                        className={CommonHelper.isNullOrEmpty(row.FactsheetUrl) ? "" : "hyper-link"}
                                        href={row.FactsheetUrl}
                                        target="_blank"
                                    >
                                        {data}
                                    </a>
                                );
                            },
                        },
                        {
                            title: "Current estimated value",
                            key: "MarketValue",
                            render: (data, row, index) => {
                                let paymentPercentage = this.props.pensionCommencementLumpSumAllocations?.find((ele) => ele.ProductId == row.ProductId)?.Percentage ?? 0;
                                let remainValue: Money =
                                {
                                    Amount: data.Amount - this.props.sellTotal * paymentPercentage / 100,
                                    Currency: data.Currency
                                }
                                return MoneyDisplayHelper.getMoneyFormat(remainValue);
                            },
                        },
                        {
                            title: "Percentage of switch amount",
                            key: "ProductId",
                            render: (data, row: ChooseAssetsToSwitchAssets, index) => {
                                const percentage =
                                    this.props.data.find((ele) => ele.ProductId == row.ProductId)?.Percentage || null;
                                let paymentPercentage = this.props.pensionCommencementLumpSumAllocations?.find((ele) => ele.ProductId == row.ProductId)?.Percentage ?? 0;
                                let currentEstimatedValue = (row.MarketValue?.Amount || 0) - this.props.sellTotal * paymentPercentage / 100;

                                const infoAmount = row.SwitchAll
                                    ? currentEstimatedValue
                                    : (percentage || 0) * totalValue * 0.01;
                                return (
                                    <PercentageTextBoxEditor
                                        value={percentage}
                                        onChange={(v) => this.onAssetsChanges(row, v)}
                                        maxNumberOfDecimalPlaces={{
                                            errorMessage: DrawdownSwitchValidationMessage.allowDecimals,
                                            value: 2,
                                        }}
                                        disabled={this.disableInput(row)}
                                        validationRules={[
                                            {
                                                errorMessage: DrawdownSwitchValidationMessage.moreThan100PercentSwitchFrom,
                                                validator: (v) => {
                                                    return v <= 100;
                                                },
                                            },
                                            {
                                                errorMessage: DrawdownSwitchValidationMessage.exceed95ofTotal,
                                                validator: (v) => {
                                                    let amount = (v || 0) * totalValue * 0.01;
                                                    return amount < currentEstimatedValue * 0.95 || row.SwitchAll;
                                                },
                                            },
                                            {
                                                errorMessage: DrawdownSwitchValidationMessage.greaterThanEstimated,
                                                validator: (v) => {
                                                    let amount = (v || 0) * totalValue * 0.01;
                                                    amount = Number(amount.toFixed(2));
                                                    return amount <= currentEstimatedValue || row.SwitchAll;
                                                },
                                            },
                                            {
                                                errorMessage: DrawdownSwitchValidationMessage.equalEstimatedValue,
                                                validator: (v) => {
                                                    let amount = (v || 0) * totalValue * 0.01;
                                                    amount = Number(amount.toFixed(2));
                                                    return (
                                                        amount !== currentEstimatedValue ||
                                                        (Math.abs(currentEstimatedValue - amount) < 0.1 && row.SwitchAll)
                                                    );
                                                },
                                            },
                                        ]}
                                        infoText={MoneyDisplayHelper.getMoneyFormat({
                                            Amount: infoAmount,
                                        })
                                        }
                                        onValidation={(errors) => this.handleValidation(errors)}
                                    />
                                );
                            },
                        },
                        {
                            thCssWrapper: "cell-right pr-0",
                            tdCssWrapper: "cell-right pr-0",
                            title: "Switch All",
                            key: "SwitchAll",
                            render: (data, row: ChooseAssetsToSwitchAssets, index) => {
                                const percentage =
                                    this.props.data.find((ele) => ele.ProductId == row.ProductId)?.Percentage || 0;
                                return (
                                    <CheckboxEditor
                                        cssWrapper={"square-checkbox mb-0"}
                                        onChange={(v) => {
                                            this.props.onSwitchAllChange(row, v);
                                        }}
                                        value={data}
                                        disabled={this.disableSwitchAll(row)}
                                    />
                                );
                            },
                        }
                    ]}
                    TotalBody={[
                        { data: "Total", colSpan: 2 },
                        {
                            data: <span className={total == 100 ? "text-positive" : "text-text"}>{total}%</span>,
                            colSpan: 2,
                        },
                    ]}
                />
            </>
        );
    }
}
